import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../components/ui/sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { CalendarCheck, Layers, CopyPlus, Send, Undo2, MousePointerClick, BellRing, Save } from 'lucide-react';
import { DayEditorSheet } from '../components/availability/DayEditorSheet';
import { BulkActionsDialog } from '../components/availability/BulkActionsDialog';
import { SubmitDialog } from '../components/availability/SubmitDialog';
import { AvailabilityLegend } from '../components/availability/AvailabilityLegend';
import {
  STATE_STYLES, SUB_STATUS_STYLES, SHIFT_META, dayState, isDayComplete,
  fmtShortDate, fmtDateTime, groupIntoWeeks, WEEKDAY_LABELS,
} from '../components/availability/constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SHIFT_CODE = Object.fromEntries(SHIFT_META.map((s) => [s.key, s.code]));

const DayCell = ({ date, entry, selected, selectMode, onClick }) => {
  if (!date) return <div className="min-h-[72px]" />;
  const state = dayState(entry);
  const s = STATE_STYLES[state];
  const dayNum = new Date(date + 'T00:00:00').getDate();
  const shiftCodes = entry && !entry.unavailable_all_day
    ? Object.entries(entry.shifts || {}).filter(([, v]) => v !== 'unavailable').map(([k]) => SHIFT_CODE[k])
    : [];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-[72px] w-full rounded-lg border p-1.5 text-left transition-all hover:shadow-md relative ${
        selected ? 'ring-2 ring-primary ring-offset-1' : ''
      }`}
      style={{ background: s.bg, borderColor: selected ? '#1D9E75' : s.border }}
      data-testid={`avail-day-${date}`}
    >
      {selectMode && (
        <span className={`absolute top-1 right-1 w-4 h-4 rounded-full border-2 ${selected ? 'bg-primary border-primary' : 'bg-white border-gray-400'}`} />
      )}
      <div className="text-xs font-bold" style={{ color: s.text }}>{dayNum}</div>
      {entry?.unavailable_all_day ? (
        <div className="text-[10px] font-semibold mt-1" style={{ color: s.text }}>All day off</div>
      ) : shiftCodes.length > 0 ? (
        <div className="flex flex-wrap gap-0.5 mt-1">
          {shiftCodes.map((c) => (
            <span key={c} className="text-[9px] font-semibold px-1 rounded" style={{ background: 'rgba(255,255,255,0.7)', color: s.text }}>{c}</span>
          ))}
        </div>
      ) : (
        <div className="text-[10px] mt-1" style={{ color: s.text }}>{state === 'no_response' ? '—' : s.label}</div>
      )}
      {entry?.restriction && <div className="text-[9px] italic truncate mt-0.5" style={{ color: s.text }}>{entry.restriction}</div>}
    </button>
  );
};

export default function MyAvailability() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(true);
  const [notLinked, setNotLinked] = useState(false);
  const [editorDate, setEditorDate] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [copyConfirmOpen, setCopyConfirmOpen] = useState(false);
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPeriods = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/availability/my`);
      setPeriods(res.data.periods);
      if (res.data.periods.length > 0) {
        setPeriodId((prev) => prev && res.data.periods.some((p) => p.period.id === prev) ? prev : res.data.periods[0].period.id);
      }
    } catch (err) {
      if (err.response?.status === 403) setNotLinked(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDetail = useCallback(async (pid) => {
    if (!pid) return;
    try {
      const res = await axios.get(`${API_URL}/api/availability/my/${pid}`);
      setDetail(res.data);
      setDays(res.data.days || {});
    } catch (err) {
      console.error('detail fetch failed', err);
    }
  }, []);

  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);
  useEffect(() => { fetchDetail(periodId); }, [periodId, fetchDetail]);

  const persistDays = async (newDays, successMsg = 'Saved') => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/availability/my/${periodId}`, {
        days: newDays, general_notes: detail?.general_notes || '',
      });
      setDetail((prev) => ({ ...prev, ...res.data }));
      setDays(res.data.days || {});
      toast.success(successMsg);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDaySave = (date, entry) => {
    const newDays = { ...days };
    if (entry === null) delete newDays[date];
    else newDays[date] = entry;
    persistDays(newDays, entry === null ? 'Day cleared' : 'Day saved');
  };

  const handleBulkApply = (targetDates, entry) => {
    const newDays = { ...days };
    targetDates.forEach((d) => {
      if (entry === null) delete newDays[d];
      else newDays[d] = { ...entry, shifts: { ...entry.shifts } };
    });
    setSelectedDates(new Set());
    setSelectMode(false);
    persistDays(newDays, `Applied to ${targetDates.length} dates`);
  };

  const handleSubmit = async (notes) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/availability/my/${periodId}/submit`, {
        days, general_notes: notes,
      });
      setDetail((prev) => ({ ...prev, ...res.data }));
      setDays(res.data.days || {});
      setSubmitOpen(false);
      toast.success('Availability submitted');
      fetchPeriods();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/availability/my/${periodId}/withdraw`);
      setDetail((prev) => ({ ...prev, ...res.data }));
      toast.success('Submission withdrawn — it is now a draft again');
      fetchPeriods();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdraw failed');
    }
  };

  const handleCopyPrevious = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/availability/my/${periodId}/copy-previous`);
      setDetail((prev) => ({ ...prev, ...res.data }));
      setDays(res.data.days || {});
      toast.success('Previous pattern copied — review and adjust as needed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nothing to copy');
    }
  };

  const handleDayClick = (date) => {
    if (selectMode) {
      setSelectedDates((prev) => {
        const next = new Set(prev);
        if (next.has(date)) next.delete(date); else next.add(date);
        return next;
      });
    } else {
      setEditorDate(date);
      setEditorOpen(true);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>;
  }

  if (notLinked) {
    return (
      <Card className="p-10 text-center max-w-lg mx-auto" data-testid="not-linked-card">
        <CalendarCheck className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <h2 className="text-lg font-semibold mb-1">Account not linked</h2>
        <p className="text-sm text-muted-foreground">Your login is not linked to a roster employee. Ask an administrator to create or link your staff account from the Team page.</p>
      </Card>
    );
  }

  if (periods.length === 0) {
    return (
      <Card className="p-10 text-center max-w-lg mx-auto" data-testid="no-periods-card">
        <CalendarCheck className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <h2 className="text-lg font-semibold mb-1">No availability periods yet</h2>
        <p className="text-sm text-muted-foreground">When an admin opens a scheduling period, it will appear here.</p>
      </Card>
    );
  }

  const stats = detail?.stats;
  const period = detail?.period;
  const status = detail?.status || 'not_started';
  const statusMeta = SUB_STATUS_STYLES[status] || SUB_STATUS_STYLES.not_started;
  const editable = !!detail?.is_editable;
  const weeks = detail ? groupIntoWeeks(detail.dates || []) : [];

  return (
    <div className="space-y-5 pb-24" data-testid="my-availability-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">My Availability</h1>
          <p className="text-sm text-muted-foreground mt-1">Tell the scheduler when you can work.</p>
        </div>
        {periods.length > 1 && (
          <Select value={periodId || ''} onValueChange={setPeriodId}>
            <SelectTrigger className="w-full sm:w-[280px] bg-background" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background">
              {periods.map((p) => (
                <SelectItem key={p.period.id} value={p.period.id}>{p.period.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {(detail?.reminders || []).map((r) => (
        <div key={r.type} className="flex items-center gap-2 rounded-lg border border-[#D99E22]/50 bg-[#FDF0D2]/60 px-4 py-3 text-sm text-[#8A6210]" data-testid={`reminder-${r.type}`}>
          <BellRing className="h-4 w-4 shrink-0" />
          {r.message}
        </div>
      ))}

      {period && (
        <Card className="p-4 sm:p-5" data-testid="availability-status-card">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div>
              <div className="text-xs text-muted-foreground">Scheduling period</div>
              <div className="text-sm font-semibold" data-testid="status-period-name">{period.name}</div>
              <div className="text-xs text-muted-foreground">{fmtShortDate(period.start_date)} – {fmtShortDate(period.end_date)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Deadline</div>
              <div className="text-sm font-semibold" data-testid="status-deadline">{fmtShortDate(period.submission_deadline)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Status</div>
              <Badge variant={statusMeta.variant} data-testid="status-badge">{statusMeta.label}</Badge>
            </div>
            <div className="min-w-[140px] flex-1">
              <div className="text-xs text-muted-foreground mb-1">
                Progress · {stats?.completed_dates}/{stats?.total_dates} dates
              </div>
              <Progress value={stats?.completion_pct || 0} className="h-2" data-testid="status-progress" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Last saved</div>
              <div className="text-xs font-medium" data-testid="status-last-saved">{fmtDateTime(detail?.last_saved_at)}</div>
            </div>
            {detail?.submitted_at && (
              <div>
                <div className="text-xs text-muted-foreground">Submitted</div>
                <div className="text-xs font-medium" data-testid="status-submitted-at">{fmtDateTime(detail.submitted_at)}</div>
              </div>
            )}
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        {editable && (
          <>
            <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} data-testid="bulk-entry-button">
              <Layers className="h-4 w-4 mr-1.5" /> Bulk Entry
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCopyConfirmOpen(true)} data-testid="copy-previous-button">
              <CopyPlus className="h-4 w-4 mr-1.5" /> Copy Previous Period
            </Button>
            <Button
              variant={selectMode ? 'default' : 'outline'} size="sm"
              onClick={() => { setSelectMode(!selectMode); setSelectedDates(new Set()); }}
              data-testid="select-dates-button"
            >
              <MousePointerClick className="h-4 w-4 mr-1.5" />
              {selectMode ? `Selecting (${selectedDates.size})` : 'Select Dates'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => persistDays(days, 'Draft saved')} disabled={saving} data-testid="save-draft-button">
              <Save className="h-4 w-4 mr-1.5" /> Save Draft
            </Button>
          </>
        )}
        {editable && (status === 'submitted' || status === 'reopened') && (
          <Button variant="outline" size="sm" onClick={() => setWithdrawConfirmOpen(true)} data-testid="withdraw-button">
            <Undo2 className="h-4 w-4 mr-1.5" /> Withdraw
          </Button>
        )}
        {editable && (
          <Button size="sm" className="ml-auto" onClick={() => setSubmitOpen(true)} data-testid="open-submit-button">
            <Send className="h-4 w-4 mr-1.5" />
            {status === 'submitted' ? 'Resubmit' : 'Submit Availability'}
          </Button>
        )}
      </div>

      <AvailabilityLegend />

      <div className="space-y-3">
        {weeks.map((week, wi) => (
          <Card key={wi} className="p-2 sm:p-3" data-testid={`avail-week-${wi}`}>
            <div className="text-[11px] font-semibold text-muted-foreground mb-1.5 px-1">Week {wi + 1}</div>
            <div className="hidden sm:grid grid-cols-7 gap-1.5 mb-1">
              {WEEKDAY_LABELS.map((l) => (
                <div key={l} className="text-[10px] font-semibold text-center text-muted-foreground uppercase">{l}</div>
              ))}
            </div>
            <div className="hidden sm:grid grid-cols-7 gap-1.5">
              {week.map((d, i) => (
                <DayCell key={i} date={d} entry={d ? days[d] : null} selected={d ? selectedDates.has(d) : false} selectMode={selectMode} onClick={() => d && handleDayClick(d)} />
              ))}
            </div>
            {/* Mobile: stacked rows with big tap targets */}
            <div className="sm:hidden space-y-1.5">
              {week.filter(Boolean).map((d) => {
                const entry = days[d];
                const state = dayState(entry);
                const s = STATE_STYLES[state];
                return (
                  <button
                    key={d} type="button" onClick={() => handleDayClick(d)}
                    className={`w-full min-h-[52px] rounded-lg border px-3 py-2 flex items-center justify-between ${selectedDates.has(d) ? 'ring-2 ring-primary' : ''}`}
                    style={{ background: s.bg, borderColor: s.border }}
                    data-testid={`avail-day-mobile-${d}`}
                  >
                    <div className="text-left">
                      <div className="text-sm font-semibold" style={{ color: s.text }}>
                        {new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </div>
                      {entry?.restriction && <div className="text-[10px] italic" style={{ color: s.text }}>{entry.restriction}</div>}
                    </div>
                    <div className="flex items-center gap-1">
                      {entry && !entry.unavailable_all_day && Object.entries(entry.shifts || {}).filter(([, v]) => v !== 'unavailable').map(([k]) => (
                        <span key={k} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white/70" style={{ color: s.text }}>{SHIFT_CODE[k]}</span>
                      ))}
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ color: s.text }}>
                        {entry?.unavailable_all_day ? 'All day off' : !isDayComplete(entry) ? 'Tap to set' : ''}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      <DayEditorSheet
        open={editorOpen} onOpenChange={setEditorOpen}
        date={editorDate} entry={editorDate ? days[editorDate] : null}
        editable={editable} onSave={handleDaySave}
      />
      <BulkActionsDialog
        open={bulkOpen} onOpenChange={setBulkOpen}
        dates={detail?.dates || []} selectedDates={selectedDates} onApply={handleBulkApply}
      />
      <SubmitDialog
        open={submitOpen} onOpenChange={setSubmitOpen}
        stats={stats} generalNotes={detail?.general_notes} onSubmit={handleSubmit} submitting={submitting}
      />

      <AlertDialog open={copyConfirmOpen} onOpenChange={setCopyConfirmOpen}>
        <AlertDialogContent data-testid="copy-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Copy previous period?</AlertDialogTitle>
            <AlertDialogDescription>
              Your most recent weekday pattern will be mapped onto this period. Date-specific notes are not copied. This overwrites what you've entered so far — you can still edit every date afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCopyPrevious} data-testid="copy-confirm-yes">Copy Pattern</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={withdrawConfirmOpen} onOpenChange={setWithdrawConfirmOpen}>
        <AlertDialogContent data-testid="withdraw-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw submission?</AlertDialogTitle>
            <AlertDialogDescription>
              Your availability will return to Draft status. Remember to resubmit before the deadline.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleWithdraw} data-testid="withdraw-confirm-yes">Withdraw</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
